import React from 'react';

import { PictureProps } from '@soluto-design/picture';
import { Button, Text, TitleProps, Title } from '@soluto-design/react';
import { LinkProps } from '@soluto-design/link';

import cls from '@soluto-design/styles/cls';

import CardBase from '../CardBase';
import Media, { MediaProps } from '../Media';

interface Person {
    name: string;
    position: string;
    linkedin?: string;
    g2?: string;
}

export interface CardTestimonialProps {
    text?: string;
    logo?: PictureProps;
    person?: Person;
    media?: MediaProps['media'];
    hasVideo?: boolean;
    onClickPlay?: () => void;
    title?: string;
    titleTag?: TitleProps['component'];
    cta?: LinkProps;
}

export default function CardTestimonial({
    title,
    titleTag = 'h3',
    text,
    logo,
    person,
    media,
    hasVideo,
    onClickPlay,
    cta,
}: CardTestimonialProps) {
    return (
        <CardBase
            className={cls(
                'rounded-large overflow-hidden bg-background-secondary flex flex-col justify-between lg:flex-row',
            )}>
            <div className="p-24 md:p-48 lg:pr-64 lg:basis-[62.5%] min-h-[510px] md:min-h-[560px] lg:min-h-[648px] flex flex-col justify-between gap-y-xxl lg:order-2">
                <div>
                    {logo?.src && (
                        <img
                            src={logo?.src}
                            alt={logo?.alt}
                            className="h-24 max-w-fit grayscale-logo"
                            loading="lazy"
                        />
                    )}
                    {!!title && (
                        <Title
                            component={titleTag}
                            className={cls(
                                'text-h400 text-text-primary mt-24',
                            )}>
                            {title}
                        </Title>
                    )}
                    {!!text && (
                        <Text
                            component="div"
                            isHtml
                            className={cls(
                                'text-b100 text-text-primary mt-16',
                            )}>
                            {text}
                        </Text>
                    )}
                </div>
                <div className="flex flex-col gap-y-xxl">
                    {!!person && (
                        <div className="flex gap-16">
                            <div className="flex gap-x-8">
                                {person?.linkedin && (
                                    <Button
                                        href={person.linkedin}
                                        size="icon"
                                        leftIcon="linkedin"
                                        variant="tertiary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                )}
                                {person?.g2 && (
                                    <Button
                                        href={person.g2}
                                        size="icon"
                                        leftIcon="g2"
                                        variant="tertiary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                )}
                            </div>
                            <div>
                                {person?.name && (
                                    <Text
                                        component="span"
                                        className="block text-b200 text-text-primary">
                                        {person.name}
                                    </Text>
                                )}
                                {person?.position && (
                                    <Text
                                        component="span"
                                        className="block text-c100 text-text-secondary pt-8">
                                        {person.position}
                                    </Text>
                                )}
                            </div>
                        </div>
                    )}
                    {!!cta && cta?.href && (
                        <Button
                            variant="tertiary"
                            size="medium"
                            rightIcon="arrow-direction--type-right"
                            {...cta}>
                            {cta.label}
                        </Button>
                    )}
                </div>
            </div>
            {media && (
                <div
                    className={cls(
                        'relative w-full h-[232px] md:h-[400px] lg:basis-[37.5%] lg:h-auto bg-base-1000 bg-opacity-50 lg:order-1',
                        hasVideo &&
                            'after:bg-background-invert after:absolute after:inset-0 after:transition-opacity after:opacity-50 cursor-pointer',
                    )}
                    {...(hasVideo && {
                        onClick: onClickPlay,
                    })}>
                    <Media media={media} layout="fill" />
                    {hasVideo && (
                        <div
                            className={cls(
                                'absolute flex items-center justify-start gap-gutter z-10 transition-opacity',
                                'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                            )}>
                            <Button
                                data-dark
                                component="button"
                                variant="primary"
                                size="icon_extra_large"
                                leftIcon="play--filled-on"
                            />
                        </div>
                    )}
                </div>
            )}
        </CardBase>
    );
}
